import type { App } from 'vue'

import { setBulkOperationsConfig } from '@/composables/use-bulk-operations/config'
import { useConfirmationService } from '@/composables/use-confirmation-service/useConfirmationService'
import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'

/**
 * Bridge the framework-layer bulk-ops adapters to the app's real toast + confirm services.
 *
 * Must run after `app.use(pinia)` (so `useToastStore()` resolves) and after
 * `app.use(ConfirmationService)` (so the confirm injection key is provided), and before
 * `app.mount(el)`.
 *
 * The confirm bridge calls `useConfirmationService()` lazily — it relies on `useConfirm()`'s
 * `inject`, which only resolves inside an injection context. Since the adapter runs detached
 * from any component setup (during dispatch), we re-establish the app's injection context with
 * `app.runWithContext()`; otherwise `useConfirm()` throws "No PrimeVue Confirmation provided!".
 */
export const installBulkOperationsAdapters = (app: App): void => {
  const toastStore = useToastStore()

  setBulkOperationsConfig({
    toast: {
      success: message => toastStore.add({ severity: 'success', summary: message }),
      warning: (message, detail) => toastStore.add({ severity: 'warn', summary: message, detail }),
      error: message => toastStore.add({ severity: 'error', summary: message }),
      info: message => toastStore.add({ severity: 'info', summary: message })
    },
    confirm: {
      prompt: ({ header, message, acceptLabel, danger, onAccept, onReject }) => {
        app.runWithContext(() => {
          const { showConfirmation } = useConfirmationService()
          showConfirmation({
            header,
            message,
            acceptProps: { label: acceptLabel, severity: danger ? 'danger' : undefined },
            accept: onAccept,
            reject: onReject
          })
        })
      }
    }
  })
}
